// feature/role-views — Admin · forensic audit log viewer.
import React, { useEffect, useMemo, useState } from "react";
import { admin, users as usersApi } from "../../api/endpoints";
import { apiBase } from "../../api/client";
import Spinner from "../../components/Spinner";
import Empty from "../../components/Empty";
import { useDebounced } from "../../components/useDebounced";
import { useCaseLauncher } from "../../components/CaseLauncher";

const ACTIONS = [
  "login","logout",
  "case.create","case.update","case.delete","case.reassign","case.flag_error","case.error_restored",
  "case.document_uploaded","case.document_deleted","case.approve_late_entry",
  "send_audit","approve","reject","decide_impugnar","start_work","set_termino_self",
  "claim_insumo","mark_solicitud_status","upload_response",
  "insumo.pdf_generated","insumo.expediente_letter","insumo.response_uploaded",
  "mark_litisoft","close",
  "user.create","user.update","user.toggle_active","user.reset_password","user.auto_reset_password",
];

const fmt = (d) => d ? new Date(d).toLocaleString("es-CO", { hour12: false }) : "—";

export default function AuditLogViewer() {
  const launcher = useCaseLauncher();
  const [allUsers, setAllUsers] = useState([]);
  const [actorId, setActorId]   = useState("");
  const [caseQ, setCaseQ]       = useState("");
  const [actionsSel, setActionsSel] = useState([]);
  const [from, setFrom]         = useState("");
  const [to, setTo]             = useState("");
  const dq = useDebounced(caseQ, 300);

  const [page, setPage] = useState(1);
  const PAGE = 50;

  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { usersApi.list().then(r => setAllUsers(r.data)); }, []);

  function load() {
    setData(null);
    const params = { limit: 500, offset: 0 };
    if (actorId) params.actor_id = actorId;
    if (dq)      params.case_q   = dq;
    if (actionsSel.length) params.action = actionsSel.join(",");
    if (from)    params.from = new Date(from).toISOString();
    if (to)      params.to   = new Date(to).toISOString();
    admin.auditLog(params).then(r => setData(r.data)).catch(() => setData([]));
  }
  useEffect(() => { load(); setPage(1); }, [actorId, dq, actionsSel, from, to]);

  const visible = useMemo(() => (data || []).slice((page - 1) * PAGE, page * PAGE), [data, page]);
  const pages = Math.max(1, Math.ceil((data?.length || 0) / PAGE));

  function toggleAction(a) {
    setActionsSel(arr => arr.includes(a) ? arr.filter(x => x !== a) : [...arr, a]);
  }

  function downloadCsv() {
    const params = {};
    if (actorId) params.actor_id = actorId;
    if (dq)      params.case_q   = dq;
    if (actionsSel.length) params.action = actionsSel.join(",");
    if (from)    params.from = new Date(from).toISOString();
    if (to)      params.to   = new Date(to).toISOString();
    const url = apiBase + admin.auditLogCsvUrl(params);
    // attach Authorization via a temporary link with token in fetch — but for download
    // we use a fetch + blob workaround so the bearer token can be sent.
    const token = localStorage.getItem("eds.token");
    fetch(url, { headers: { Authorization: "Bearer " + token } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `audit_log_${Date.now()}.csv`;
        a.click();
      });
  }

  return (
    <div className="col" style={{ gap: 16 }}>
      <div className="row">
        <h1 style={{ margin: 0 }}>Registro de auditoría</h1>
        <div style={{ flex: 1 }} />
        <button className="secondary" onClick={downloadCsv}>Descargar CSV</button>
      </div>
      <div className="muted" style={{ marginTop: -8 }}>
        Registro inmutable de toda acción de modificación. IP y user-agent capturados en cada evento.
      </div>

      <div className="card tight">
        <div className="grid-4">
          <div>
            <label>Actor</label>
            <select value={actorId} onChange={(e) => setActorId(e.target.value)}>
              <option value="">Todos</option>
              {allUsers.map(u => <option key={u.id} value={u.id}>{u.name} · {u.role}</option>)}
            </select>
          </div>
          <div>
            <label>Caso (radicado o número)</label>
            <input value={caseQ} onChange={(e) => setCaseQ(e.target.value)} placeholder="TUT-2026 o RAD-…" />
          </div>
          <div>
            <label>Desde</label>
            <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label>Hasta</label>
            <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <label>Acciones</label>
          <div className="row" style={{ flexWrap: "wrap", gap: 6 }}>
            {ACTIONS.map(a => {
              const on = actionsSel.includes(a);
              return (
                <span
                  key={a}
                  className={"badge " + (on ? "mint" : "")}
                  style={{ cursor: "pointer", border: on ? undefined : "1px solid var(--border-hairline)" }}
                  onClick={() => toggleAction(a)}
                >
                  {a}
                </span>
              );
            })}
            {actionsSel.length > 0 && (
              <button className="ghost sm" onClick={() => setActionsSel([])}>Limpiar</button>
            )}
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {data == null ? (
          <div style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
        ) : !data.length ? (
          <Empty title="Sin eventos" message="Ajuste los filtros para ampliar la búsqueda." />
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>Fecha y hora</th><th>Actor</th><th>Acción</th>
                  <th>Caso</th><th>Estado</th><th>IP</th><th>User-Agent</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(e => {
                  const open = expanded === e.id;
                  return (
                    <React.Fragment key={e.id}>
                      <tr style={{ cursor: "pointer" }} onClick={() => setExpanded(open ? null : e.id)}>
                        <td style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>{fmt(e.created_at)}</td>
                        <td>
                          {e.actor_name || <span className="muted">Sistema</span>}
                          {e.actor_role && <span className="muted" style={{ fontSize: 11 }}> · {e.actor_role}</span>}
                        </td>
                        <td><span className="badge">{e.action}</span></td>
                        <td>
                          {e.case_number ? (
                            <a href="#open" onClick={(ev) => { ev.stopPropagation(); ev.preventDefault(); launcher.open(e.case_id); }}>
                              {e.case_number}
                            </a>
                          ) : <span className="muted">—</span>}
                        </td>
                        <td className="muted" style={{ fontSize: 12 }}>
                          {e.from_state && e.to_state ? `${e.from_state} → ${e.to_state}` : (e.to_state || e.from_state || "—")}
                        </td>
                        <td style={{ fontFamily: "ui-monospace, monospace", fontSize: 11 }}>{e.ip_address || "—"}</td>
                        <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11 }} title={e.user_agent || ""}>
                          {e.user_agent || "—"}
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={7} style={{ background: "var(--bg-subtle)", padding: 16 }}>
                            <div className="label-uc" style={{ marginBottom: 6 }}>Payload</div>
                            <pre style={{ margin: 0, fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12, whiteSpace: "pre-wrap" }}>
                              {JSON.stringify(e.payload || {}, null, 2)}
                            </pre>
                            {e.notes && (
                              <>
                                <div className="label-uc" style={{ marginTop: 12, marginBottom: 6 }}>Notas</div>
                                <div>{e.notes}</div>
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            <div style={{ display: "flex", alignItems: "center", padding: "10px 14px", borderTop: "1px solid var(--border-hairline)" }}>
              <span className="muted" style={{ fontSize: 12 }}>{data.length} evento(s) · página {page}/{pages}</span>
              <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                <button className="secondary sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</button>
                <button className="secondary sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Siguiente</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
